/**
 * Admin API contract, shared by the Lambda handler and the admin SPA.
 * Transport-level only: no storage or UI concerns.
 */
import { z } from "zod";
import {
  type Asset,
  ExhibitionObjectSchema,
  exhibitionDatesInOrder,
  WorkSchema,
  type ExhibitionDocument,
  type WorkDocument,
} from "@yushi-portfolio/content-schema";

// ---------------------------------------------------------------------------
// Inputs: documents minus server-managed fields
// ---------------------------------------------------------------------------

const SERVER_MANAGED = { schemaVersion: true, id: true, createdAt: true, updatedAt: true } as const;

/** Body of POST /works. */
export const WorkInputSchema = WorkSchema.omit({ ...SERVER_MANAGED, version: true });
/** Body of PUT /works/:id. `version` must equal the stored draft version. */
export const WorkUpdateSchema = WorkSchema.omit(SERVER_MANAGED);

/** Body of POST /exhibitions and PUT /exhibitions/:id (exhibitions have no version; last write wins). */
export const ExhibitionInputSchema = ExhibitionObjectSchema.omit(SERVER_MANAGED).refine(exhibitionDatesInOrder, {
  message: "endDate must not be before startDate",
  path: ["endDate"],
});

export type WorkInput = z.infer<typeof WorkInputSchema>;
export type WorkUpdate = z.infer<typeof WorkUpdateSchema>;
export type ExhibitionInput = z.infer<typeof ExhibitionInputSchema>;

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

/**
 * Upload policy. The original's extension comes from the verified MIME type,
 * never from the uploaded file name.
 */
export const UPLOAD_TYPES = {
  "image/jpeg": { type: "image", extension: "jpg", maxBytes: 200 * 1024 ** 2 },
  "image/png": { type: "image", extension: "png", maxBytes: 200 * 1024 ** 2 },
  "image/tiff": { type: "image", extension: "tif", maxBytes: 500 * 1024 ** 2 },
  "image/webp": { type: "image", extension: "webp", maxBytes: 100 * 1024 ** 2 },
  // Web-ready video only; there is no transcoding.
  "video/mp4": { type: "video", extension: "mp4", maxBytes: 2 * 1024 ** 3 },
  "video/webm": { type: "video", extension: "webm", maxBytes: 2 * 1024 ** 3 },
  "application/pdf": { type: "document", extension: "pdf", maxBytes: 100 * 1024 ** 2 },
} as const;

export type UploadContentType = keyof typeof UPLOAD_TYPES;

export const UploadRequestSchema = z.object({
  contentType: z.enum(Object.keys(UPLOAD_TYPES) as [UploadContentType, ...UploadContentType[]], {
    message: "unsupported file type (JPEG, PNG, TIFF, WebP, MP4, WebM or PDF)",
  }),
  size: z.number().int().positive(),
});

export type UploadRequest = z.infer<typeof UploadRequestSchema>;

/** Browser POSTs the file to `url` as multipart form data: `fields` first, then `file`. */
export type UploadResponse = {
  assetId: string;
  url: string;
  fields: Record<string, string>;
  maxBytes: number;
};

/** Body of POST /assets, sent after the upload finished. */
export const AssetRegistrationSchema = z.object({
  assetId: z.string().regex(/^ast-[a-f0-9-]{36}$/),
  contentType: UploadRequestSchema.shape.contentType,
  originalFilename: z.string().max(255).optional(),
  /** Measured in the browser for video (images are measured when processed). */
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  durationSeconds: z.number().nonnegative().optional(),
  photographer: z.string().max(200).optional(),
  copyright: z.string().max(200).optional(),
});

export type AssetRegistration = z.infer<typeof AssetRegistrationSchema>;

/** Body of PUT /assets/:id: facts about the file that people may edit. */
export const AssetUpdateSchema = z.object({
  photographer: z.string().max(200).optional(),
  copyright: z.string().max(200).optional(),
});

export type AssetUpdate = z.infer<typeof AssetUpdateSchema>;

export type AssetListResponse = { items: Asset[] };

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export type ListItem = {
  id: string;
  slug?: string;
  title: string;
  updatedAt?: string;
  published: boolean;
};

export type ListResponse = { items: ListItem[] };

export type EditableResponse<T> = {
  draft: T;
  /** Present once the document has been published. */
  published?: T;
};

export type WorkResponse = EditableResponse<WorkDocument>;
export type ExhibitionResponse = EditableResponse<ExhibitionDocument>;

// ---------------------------------------------------------------------------
// Publishing
// ---------------------------------------------------------------------------

/** Body of POST /works/:id/publish: the draft version the publisher reviewed. */
export const PublishRequestSchema = z.object({ version: z.number().int().nonnegative().optional() });
export type PublishRequest = z.infer<typeof PublishRequestSchema>;

export type BuildInfo = {
  id: string;
  status: "IN_PROGRESS" | "SUCCEEDED" | "FAILED" | "FAULT" | "STOPPED" | "TIMED_OUT" | string;
  startTime?: string;
  endTime?: string;
};

export type PublishResponse<T> = {
  published: T;
  revision: RevisionItem;
  /** Site generation requested by this publish, and whether a build was started now. */
  build: { generation: number; started: boolean };
};

export type RevisionItem = { publishedAt: string; version?: number; publishedBy?: string };
export type RevisionsResponse = { items: RevisionItem[] };

export type PublishStatusResponse = {
  requested: number;
  built: number;
  /** True while the public site lags behind the latest publish. */
  pending: boolean;
  lastBuild?: BuildInfo;
};

export type ApiErrorCode =
  | "bad_request"
  | "validation_failed"
  | "not_found"
  | "version_conflict"
  | "slug_taken"
  | "upload_missing"
  | "not_implemented"
  | "internal";

export type ApiError = {
  error: { code: ApiErrorCode; message: string; details?: string[] };
};
